import moment from 'moment';
import * as XLSX from 'xlsx';

import { DATE_FORMAT } from '_utils/constant';
import { convertToGMT0, readFileToCreateEvent } from '_utils/function';

import { DEFAULT_EVENT_FORM_VALUES } from '../constants/eventForm';
import { initializeForm } from './useFetchEventDetail';

/**
 * Wires up "Download template" (src/pages/Event/HostedEvent's Share Template
 * export) -> "Upload" on this form. Reuses the same parser the old
 * src/pages/Event/Create page uses (readFileToCreateEvent), then feeds the
 * result through the same initializeForm mapper used to pre-fill this form
 * from a fetched event (useFetchEventDetail).
 *
 * initializeForm expects closingTime/pickupTime as GMT0 strings (it converts
 * them back to local internally). The template format only carries calendar
 * dates, not times - readFileToCreateEvent always bakes both closing and
 * pickup time-of-day to END_DAY (23:59) - and initializeForm derives its
 * "timeTo" field from pickupTimestamp/pickupDuration instead, which this
 * flow doesn't have, so timeTo is set to 23:59 explicitly after
 * initializeForm runs (matching what the file actually encodes; the host
 * can still adjust it before publishing). initializeForm also resets the
 * whole form via react-hook-form's reset(), which would clear the
 * already-selected group (the template has no group field), so the current
 * groupId is restored right after. Same for payTogether: the template has
 * no such field, so initializeForm defaults it to false (falsy) instead of
 * this form's actual default (true) - reset explicitly so an upload
 * doesn't silently turn it off.
 */
export default function useUploadEventTemplate({ reset, setValue, addToast, groupId }) {
  const handleUploadTemplate = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsedEvent = readFileToCreateEvent(sheet);

        const closingTimeOfDay = moment(parsedEvent.closingTime, DATE_FORMAT).format('HH:mm');

        initializeForm(
          {
            ...parsedEvent,
            closingTime: convertToGMT0(parsedEvent.closingTime),
            pickupTime: convertToGMT0(parsedEvent.pickupTime),
          },
          reset
        );
        setValue('timeTo', closingTimeOfDay);
        setValue('payTogether', DEFAULT_EVENT_FORM_VALUES.payTogether);
        if (groupId) {
          setValue('groupId', groupId);
        }

        if (addToast) {
          addToast('Template loaded - form has been pre-filled', {
            appearance: 'success',
            autoDismiss: true,
          });
        }
      } catch (error) {
        if (addToast) {
          addToast('Please check the uploaded file', { appearance: 'error', autoDismiss: true });
        }
      }
    };

    reader.onerror = () => {
      if (addToast) {
        addToast('Failed to read file', { appearance: 'error', autoDismiss: true });
      }
    };

    reader.readAsBinaryString(file);
  };

  return handleUploadTemplate;
}
